import { sameJson } from "../document/same-json.ts";
import type { WorldDocument } from "../document/types.ts";
import type { OpIds } from "./command-ops.ts";
import { LIVE_OPS_VERSION, type LiveOp } from "./op-types.ts";
import { cellsOf } from "./ops.ts";
import type { LiveServerMessage } from "./protocol.ts";
import { liveRepaintOf } from "./repaint.ts";
import { resolveOp } from "./resolve.ts";
import type { IntentOutcome, SkipReason } from "./resolve-types.ts";
import { comparedCheck, withCheckpoint, type CheckResult } from "./session-check.ts";

export { checksEnded } from "./session-check.ts";
import { afterTilesets, withAnswers, withEdit } from "./session-history.ts";
import { OFFLINE_EDIT_MS, OP_SEND_INTERVAL_MS, PENDING_OPS_MAX, RATE_CELLS_PER_TOKEN, RATE_TOKENS_PER_SECOND, type CheckStep, type EditPart, type LiveSession, type PendingOp, type SessionRefusal, type SessionSaveState, type SessionStep, type SettledOp } from "./session-types.ts";

/**
 * A tab's live session as a pure reducer (ADR-086 decisions 26 and 28): the server's document as far as the
 * tab has heard (`confirmed`), the operations it sent or will send and has no answer for (`pending`), and the
 * document the editor shows, which is always the confirmed one with the pending operations resolved on top.
 * It holds no store, no socket and no timer: the host hands it the operations of each local command, each
 * frame the server sends, a snapshot when it asks for one and the time, and sends what a step returns.
 *
 * A remote operation moves the confirmed document and the pending ones are resolved again on top of it, so the
 * tab's own edit shows at once and stays through whatever arrives before its answer. An `ack` moves the tab's
 * own operation into the confirmed document. The later sequence stands, in every tab, since each resolves the
 * same operations in the same order.
 *
 * The session also keeps the tab's own history (`session-history.ts`, `session-undo.ts`): each local edit with
 * the commands its operations resolved to, for undo and redo.
 */

/**
 * What a session opens on: the document the tab loaded, the tab's tag, whether it may send operations, the
 * room's epoch and sequence the load named (null and 0 for a head read before any room was open, with the
 * head's revision), and the time.
 */
export interface SessionOpening {
  readonly document: WorldDocument;
  readonly tag: string;
  readonly writer: boolean;
  readonly epoch: string | null;
  readonly seq: number;
  readonly revision: number | null;
  readonly now: number;
}

/**
 * What a snapshot read over HTTP gives: the room's document with the epoch and sequence it is at.
 */
export interface SessionSnapshot {
  readonly document: WorldDocument;
  readonly epoch: string;
  readonly seq: number;
}

/**
 * Opens a session over a loaded document, not yet in the stream.
 *
 * @param opening The document, the tab's tag and what the load said of the room.
 * @returns The session: nothing pending, its first operation taking counter 1.
 */
export function openSession(opening: SessionOpening): LiveSession {
  return { tag: opening.tag, next: 1, writer: opening.writer, confirmed: opening.document, epoch: opening.epoch, seq: opening.seq, revision: opening.revision, pending: [], shown: opening.document, link: "away", stop: null, target: 0, awaySince: opening.now, sentAt: null, holdUntil: 0, past: [], future: [], checks: [], checking: true, distrusted: false };
}

/**
 * When a session's next waiting operation may be sent.
 *
 * @param session The session.
 * @returns The time, or null when nothing waits or the session sends nothing as it stands.
 */
function wakeOf(session: LiveSession): number | null {
  if (session.link !== "live" || session.stop !== null || !session.writer || !session.pending.some((held) => !held.sent)) {
    return null;
  }
  return Math.max(session.holdUntil, session.sentAt === null ? session.holdUntil : session.sentAt + OP_SEND_INTERVAL_MS);
}

/**
 * A step's result with everything a step may leave out filled in.
 *
 * @param session The session after the step.
 * @param extra What the step sends, repaints, settles or refuses.
 * @returns The step.
 */
export function stepOf(session: LiveSession, extra: Partial<Omit<SessionStep, "session" | "wake">> = {}): SessionStep {
  return { session, send: extra.send ?? [], repaint: extra.repaint ?? [], settled: extra.settled ?? [], outcomes: extra.outcomes ?? [], unkept: extra.unkept ?? [], refused: extra.refused ?? null, historyDropped: extra.historyDropped ?? 0, wake: wakeOf(session), diverged: extra.diverged ?? null };
}

/**
 * The document a tab shows: its pending operations resolved in order on top of a confirmed document.
 *
 * @param confirmed The confirmed document.
 * @param pending The pending operations.
 * @returns The shown document.
 */
function shownOver(confirmed: WorldDocument, pending: readonly PendingOp[]): WorldDocument {
  return pending.reduce((document, held) => resolveOp(document, held.op).document, confirmed);
}

/**
 * The ids the next local command's operations take.
 *
 * @param session The session.
 * @returns The tab's tag and the next counter, for `commandToOps`.
 */
export function sessionIds(session: LiveSession): OpIds {
  return { tag: session.tag, next: session.next };
}

/**
 * Why a session takes no command now.
 *
 * @param session The session.
 * @param now The time.
 * @returns The reason, or null when it takes one: a tab that may only view, a stopped session, one without the
 *   stream for longer than `OFFLINE_EDIT_MS`, or one holding `PENDING_OPS_MAX` operations unanswered.
 */
export function editRefusal(session: LiveSession, now: number): SessionRefusal | null {
  if (!session.writer) {
    return "view";
  }
  if (session.stop !== null) {
    return session.stop;
  }
  if (session.awaySince !== null && now - session.awaySince > OFFLINE_EDIT_MS) {
    return "offline";
  }
  return session.pending.length >= PENDING_OPS_MAX ? "full" : null;
}

/**
 * What the save cell reads.
 *
 * @param session The session.
 * @param now The time.
 * @returns `paused` once the tab has been without the stream longer than `OFFLINE_EDIT_MS`, else `sending`
 *   while an operation has no answer, else `live`.
 */
export function sessionSaveState(session: LiveSession, now: number): SessionSaveState {
  if (session.awaySince !== null && now - session.awaySince > OFFLINE_EDIT_MS) {
    return "paused";
  }
  return session.pending.length > 0 ? "sending" : "live";
}

/**
 * Queues operations of the tab's own: each is resolved into the shown document and waits to be sent.
 *
 * @param session The session; never written into.
 * @param ops The operations, in order, under the session's next ids.
 * @returns The session with them pending, each operation's outcomes, and each with the commands it resolved to
 *   against the shown document.
 */
export function queued(session: LiveSession, ops: readonly LiveOp[]): { readonly session: LiveSession; readonly outcomes: readonly (readonly IntentOutcome[])[]; readonly parts: readonly EditPart[] } {
  let shown = session.shown;
  const outcomes: (readonly IntentOutcome[])[] = [];
  const parts: EditPart[] = [];
  for (const op of ops) {
    const resolved = resolveOp(shown, op);
    shown = resolved.document;
    outcomes.push(resolved.outcomes);
    parts.push({ id: op.id, commands: resolved.commands });
  }
  const pending = [...session.pending, ...ops.map((op): PendingOp => ({ op, sent: false, resent: false }))];
  return { session: { ...session, next: session.next + ops.length, pending, shown }, outcomes, parts };
}

/**
 * Why a session does not take these operations now.
 *
 * @param session The session.
 * @param ops The operations.
 * @param now The time.
 * @returns The reason of `editRefusal`, `full` when they would pass `PENDING_OPS_MAX`, `ids` when they do not
 *   carry the tab's tag and consecutive counters from the session's next, or null.
 */
export function opsRefusal(session: LiveSession, ops: readonly LiveOp[], now: number): SessionRefusal | null {
  return editRefusal(session, now) ?? (session.pending.length + ops.length > PENDING_OPS_MAX ? "full" : ops.every((op, i) => op.id === `${session.tag}-${session.next + i}`) ? null : "ids");
}

/**
 * Takes the operations of one local command, already built by `commandToOps` from `sessionIds` and checked by
 * the store: they show at once, wait to be sent, and are recorded as one edit of the tab's own to undo.
 *
 * @param session The session; never written into.
 * @param ops The operations, in order, carrying the tab's tag and consecutive counters from the session's next.
 * @param now The time.
 * @returns The session with them pending and resolved into the shown document, each operation's outcomes and
 *   what to draw again; or the session as given with the reason it takes no command (`editRefusal`), `ids`
 *   when the operations do not carry the session's ids, or `full` when they would pass `PENDING_OPS_MAX`.
 */
export function takeLocalOps(session: LiveSession, ops: readonly LiveOp[], now: number): SessionStep {
  const refused = opsRefusal(session, ops, now);
  const first = ops[0];
  if (refused !== null || first === undefined) {
    return stepOf(session, { refused });
  }
  const taken = queued(session, ops);
  const recorded = afterTilesets(session.shown, withEdit(taken.session, { group: first.group, parts: taken.parts }));
  return stepOf(recorded.session, { outcomes: taken.outcomes, repaint: liveRepaintOf(session.shown, recorded.session.shown), historyDropped: recorded.dropped });
}

/**
 * Sends the next waiting operation when its time has come: one frame a call, at least `OP_SEND_INTERVAL_MS`
 * after the last and never before a refusal for the rate has been waited out.
 *
 * @param session The session; never written into.
 * @param now The time.
 * @returns The session with that operation sent and its frame, or the session as given when none is due;
 *   `wake` is when to call again.
 */
export function pollSession(session: LiveSession, now: number): SessionStep {
  const due = wakeOf(session);
  const index = session.pending.findIndex((held) => !held.sent);
  const waiting = session.pending[index];
  if (due === null || now < due || waiting === undefined) {
    return stepOf(session);
  }
  const pending = session.pending.map((held, i) => (i === index ? { ...held, sent: true } : held));
  return stepOf({ ...session, pending, sentAt: now }, { send: [{ t: "op", ...waiting.op }] });
}

/**
 * Notes that the socket closed: the session is without the stream, and whatever it had sent may or may not
 * have arrived, so every pending operation waits to be sent again.
 *
 * @param session The session; never written into.
 * @param now The time.
 * @returns The session; it goes on taking commands for `OFFLINE_EDIT_MS` from when it first lost the stream.
 */
export function streamLost(session: LiveSession, now: number): LiveSession {
  return { ...session, link: "away", awaySince: session.awaySince ?? now, holdUntil: 0, pending: session.pending.map((held) => ({ ...held, sent: false, resent: held.resent || held.sent })) };
}

/**
 * The session asking for a snapshot: what it holds cannot be caught up from.
 *
 * @param session The session.
 * @param now The time.
 * @returns The session, without the stream until the snapshot is adopted.
 */
function wanting(session: LiveSession, now: number): LiveSession {
  return { ...session, link: "snapshot", awaySince: session.awaySince ?? now };
}

/**
 * Adopts a snapshot the host read: the confirmed document is replaced, the pending operations are resolved
 * again on top of it, and the session says what it now holds.
 *
 * @param session The session; never written into.
 * @param snapshot The room's document with its epoch and sequence.
 * @param now The time.
 * @returns The session, `resume` to send unless the socket is closed, and what to draw again. Every pending
 *   operation waits to be sent again under its id, since the snapshot may hold it already. A snapshot of the
 *   session's own epoch that is older than what the session holds, when the session wants none, changes
 *   nothing: it was read for a want the session has since caught up without, and adopting it would roll the
 *   tab back. A session a check found diverged adopts whatever the server's snapshot holds and is trusted
 *   again; the checks it held are let go, since they speak of a document it no longer holds.
 */
export function adoptSnapshot(session: LiveSession, snapshot: SessionSnapshot, now: number): SessionStep {
  if (session.link !== "snapshot" && !session.distrusted && session.epoch === snapshot.epoch && snapshot.seq < session.seq) {
    return stepOf(session);
  }
  const pending = session.pending.map((held) => ({ ...held, sent: false, resent: held.resent || held.sent }));
  const shown = shownOver(snapshot.document, pending);
  const kept = afterTilesets(session.shown, { ...session, confirmed: snapshot.document, epoch: snapshot.epoch, seq: snapshot.seq, revision: null, pending, shown, checks: [], distrusted: false });
  const adopted = kept.session;
  const repaint = liveRepaintOf(session.shown, shown);
  if (session.link === "away" || session.stop === "version") {
    return stepOf(adopted, { repaint, historyDropped: kept.dropped });
  }
  const live = snapshot.seq >= session.target;
  return stepOf({ ...adopted, link: live ? "live" : "catching", awaySince: live ? null : (session.awaySince ?? now) }, { repaint, historyDropped: kept.dropped, send: [{ t: "resume", v: LIVE_OPS_VERSION, epoch: snapshot.epoch, seq: snapshot.seq }] });
}

/**
 * Why the intents of one of the tab's own operations were skipped in the server's order, for an operation of
 * an edit the builder made: one on the tab's undo or redo list. An operation outside them (what a load
 * changes, sent as the tab's first operations) reports nothing, since the builder made no edit to be told
 * of: two tabs that send the same load have the second one's skipped, and neither says a word.
 *
 * @param session The session before the answer.
 * @param op The operation.
 * @param outcomes What its intents came to.
 * @returns The reasons, in order; none for an operation of no edit of the builder's.
 */
function skipsOf(session: LiveSession, op: LiveOp, outcomes: readonly IntentOutcome[]): SkipReason[] {
  const mine = session.past.some((entry) => entry.group === op.group) || session.future.some((entry) => entry.group === op.group);
  return mine ? outcomes.flatMap((outcome) => (outcome.status === "skipped" ? [outcome.reason] : [])) : [];
}

/**
 * Answers one of the tab's own operations: it and every pending operation before it leave the pending list.
 * The ones before it can never be sequenced, since the room refuses an id below one it has taken.
 *
 * @param session The session.
 * @param index The answered operation's place among the pending ones.
 * @param seq The sequence it took, or null when it took none or the server held it already.
 * @returns The step: the operation moved into the confirmed document when `seq` is the next sequence, and
 *   only dropped otherwise. `unkept` names why each of its intents was skipped in the server's order, when
 *   the operation is of an edit on the tab's undo or redo list and took a sequence or took none because
 *   nothing of it applied. With nothing left pending the confirmed document is the shown one itself, which
 *   holds the same operations resolved in the same order, so the tab keeps one copy.
 */
function answered(session: LiveSession, index: number, seq: number | null): SessionStep {
  const held = session.pending[index] as PendingOp;
  const before: SettledOp[] = session.pending.slice(0, index).map((earlier) => ({ id: earlier.op.id, group: earlier.op.group, seq: null, commands: [] }));
  const pending = session.pending.slice(index + 1);
  if (seq !== null && seq === session.seq + 1) {
    const resolved = resolveOp(session.confirmed, held.op);
    const shown = index === 0 ? session.shown : shownOver(resolved.document, pending);
    const confirmed = index === 0 && pending.length === 0 ? session.shown : resolved.document;
    const settled: SettledOp = { id: held.op.id, group: held.op.group, seq, commands: resolved.commands };
    const unkept = skipsOf(session, held.op, resolved.outcomes);
    return stepOf({ ...session, confirmed, seq, pending, shown }, { settled: [...before, settled], unkept, repaint: liveRepaintOf(session.shown, shown) });
  }
  const shown = shownOver(session.confirmed, pending);
  const none = seq === null && !held.resent;
  const settled: SettledOp = { id: held.op.id, group: held.op.group, seq: null, commands: none ? [] : null };
  const unkept = none ? skipsOf(session, held.op, resolveOp(session.confirmed, held.op).outcomes) : [];
  return stepOf({ ...session, pending, shown }, { settled: [...before, settled], unkept, repaint: liveRepaintOf(session.shown, shown) });
}

/**
 * Takes an operation the server sent on.
 *
 * @param session The session.
 * @param message The `op` message.
 * @param now The time.
 * @returns The step: nothing for a sequence already held; a snapshot wanted for one past the next; the tab's
 *   own operation settled when the message is one it holds pending (a tail after `resume` brings those); else
 *   the confirmed document moved on and the pending operations resolved again on top.
 */
function remote(session: LiveSession, message: Extract<LiveServerMessage, { readonly t: "op" }>, now: number): SessionStep {
  if (session.link === "away" || session.link === "snapshot" || session.stop === "version" || message.seq <= session.seq) {
    return stepOf(session);
  }
  if (message.seq > session.seq + 1) {
    return stepOf(wanting(session, now));
  }
  const own = session.pending.findIndex((held) => held.op.id === message.id && held.op.group === message.group && sameJson(held.op.list, message.list));
  let step: SessionStep;
  if (own >= 0) {
    step = answered(session, own, message.seq);
  } else {
    const confirmed = resolveOp(session.confirmed, message.list).document;
    const shown = shownOver(confirmed, session.pending);
    step = stepOf({ ...session, confirmed, seq: message.seq, shown }, { repaint: liveRepaintOf(session.shown, shown) });
  }
  const caught = step.session.link === "catching" && step.session.seq >= step.session.target;
  return caught ? stepOf({ ...step.session, link: "live", awaySince: null }, step) : step;
}

/**
 * Takes the room's greeting: the session says what it holds when that can be caught up from, and asks for a
 * snapshot when it cannot.
 *
 * @param session The session.
 * @param message The `stream` message.
 * @param now The time.
 * @returns The step. In step means: the room's epoch and a sequence from its floor to its own; or a head read
 *   before any room was open, when the room's revision is that head's and its floor is 0. A session a check
 *   found diverged is never in step. A server of another protocol version stops the session.
 */
function greeted(session: LiveSession, message: Extract<LiveServerMessage, { readonly t: "stream" }>, now: number): SessionStep {
  if (message.v !== LIVE_OPS_VERSION) {
    return stepOf({ ...session, stop: "version" });
  }
  const sameEpoch = session.epoch === message.epoch && session.seq >= message.floor && session.seq <= message.seq;
  const fromHead = session.epoch === null && session.seq === 0 && session.revision === message.revision && message.floor === 0;
  if ((!sameEpoch && !fromHead) || session.distrusted) {
    return stepOf(wanting({ ...session, target: message.seq }, now));
  }
  const live = session.seq >= message.seq;
  const joined: LiveSession = { ...session, epoch: message.epoch, target: message.seq, link: live ? "live" : "catching", awaySince: live ? null : (session.awaySince ?? now) };
  return stepOf(joined, { send: [{ t: "resume", v: LIVE_OPS_VERSION, epoch: message.epoch, seq: session.seq }] });
}

/**
 * How long a tab waits after an operation was refused for the rate: until the server's bucket, empty at
 * worst, can pay for the frame, which costs one token and one more for every `RATE_CELLS_PER_TOKEN` cells it
 * covers.
 *
 * @param op The refused operation.
 * @returns The wait in milliseconds.
 */
function rateWait(op: LiveOp): number {
  return Math.ceil(((1 + cellsOf(op.list) / RATE_CELLS_PER_TOKEN) * 1000) / RATE_TOKENS_PER_SECOND);
}

/**
 * Takes the server's refusal of one of the tab's operations.
 *
 * @param session The session.
 * @param message The `nack` message.
 * @param now The time.
 * @returns The step. For the rate, the operation was never weighed: it and every pending operation after it
 *   wait to be sent again, in order, under the ids they have, once the wait for the refused frame's cost has
 *   passed. The server refuses every later frame of the socket until the refused one is taken, so nothing
 *   overtakes it; a refusal for an operation that is already waiting changes nothing. Any other reason stops
 *   the session with everything still pending.
 */
function refusedByServer(session: LiveSession, message: Extract<LiveServerMessage, { readonly t: "nack" }>, now: number): SessionStep {
  if (message.reason !== "rate") {
    return stepOf({ ...session, stop: session.stop ?? message.reason });
  }
  const index = session.pending.findIndex((held) => held.op.id === message.id);
  const held = session.pending[index];
  if (held === undefined || !held.sent) {
    return stepOf(session);
  }
  const pending = session.pending.map((later, i) => (i >= index ? { ...later, sent: false } : later));
  return stepOf({ ...session, pending, holdUntil: now + rateWait(held.op) });
}

/**
 * Takes a frame the server sent, before the session's history hears of it.
 *
 * @param session The session.
 * @param message The message.
 * @param now The time.
 * @returns The step.
 */
function received(session: LiveSession, message: LiveServerMessage, now: number): SessionStep {
  switch (message.t) {
    case "stream":
      return greeted(session, message, now);
    case "op":
      return remote(session, message, now);
    case "ack": {
      const index = session.pending.findIndex((held) => held.op.id === message.id);
      if (index < 0 || session.link === "away") {
        return stepOf(session);
      }
      return message.seq !== null && message.seq > session.seq + 1 ? stepOf(session.link === "snapshot" ? session : wanting(session, now)) : answered(session, index, message.seq);
    }
    case "nack":
      return refusedByServer(session, message, now);
    case "resync":
      return stepOf(session.link === "away" ? session : wanting(session, now));
    case "frozen":
      return stepOf({ ...session, stop: session.stop ?? "frozen" });
    case "checkpoint":
      return stepOf(withCheckpoint(session, message));
    default:
      return stepOf(session);
  }
}

/**
 * Takes a frame the server sent.
 *
 * @param session The session; never written into.
 * @param message The message, as `parseServerMessage` read it.
 * @param now The time.
 * @returns The step: the session after it, what to send, what to draw again and which of the tab's own
 *   operations it settled, whose commands the history now holds as the server's order gave them. A message
 *   that is not the stream's own business (presence, claims, versions, library notices) leaves the session
 *   as given.
 */
export function receiveServerMessage(session: LiveSession, message: LiveServerMessage, now: number): SessionStep {
  const step = received(session, message, now);
  const kept = afterTilesets(session.shown, withAnswers(step.session, step.settled));
  return kept.session === step.session ? step : { ...step, session: kept.session, historyDropped: kept.dropped };
}

/**
 * Takes the digest the host computed for a check (ADR-086 decision 33): the SHA-256 of the text of the
 * confirmed document the session held at a checkpoint's sequence.
 *
 * @param session The session; never written into.
 * @param result The check's epoch and sequence with the digest.
 * @param now The time.
 * @returns The step and the verdict. `matched`: the check and every older one are let go and nothing else
 *   changes. `diverged`: the step names the sequence, every check is let go, the session is distrusted, and a
 *   session with the stream and not stopped asks for a snapshot, its pending operations kept to be resolved
 *   on the server's document and sent again; one without the stream asks when it is greeted again. `stale`:
 *   the session holds no such check, and is returned as given.
 */
export function checkCompared(session: LiveSession, result: CheckResult, now: number): CheckStep {
  const compared = comparedCheck(session, result);
  if (compared === null) {
    return { ...stepOf(session), verdict: "stale" };
  }
  if (compared.equal) {
    return { ...stepOf({ ...session, checks: compared.rest }), verdict: "matched" };
  }
  const distrusted: LiveSession = { ...session, checks: [], distrusted: true };
  const asks = (session.link === "live" || session.link === "catching") && session.stop === null;
  return { ...stepOf(asks ? wanting(distrusted, now) : distrusted, { diverged: compared.check.seq }), verdict: "diverged" };
}
